import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export { dayjs };
export type { Dayjs } from 'dayjs';

export const isValidDayjs = (dayjsObj: dayjs.Dayjs | null): boolean => dayjsObj?.isValid() ?? false;

export const parseDateString = (dateString: string, format: string): dayjs.Dayjs | null => {
  if (!dateString) {
    return null;
  }

  try {
    return dayjs(dateString, format, true);
  } catch {
    return null;
  }
};

export const parseTimeString = (timeString: string, format: string): dayjs.Dayjs | null => {
  if (!timeString) {
    return null;
  }

  try {
    return dayjs(timeString, format, true);
  } catch {
    return null;
  }
};

export const parseDateTimeString = (
  dateString: string,
  timeString: string,
  dateFormat: string,
  timeFormat: string
): dayjs.Dayjs | null => {
  if (!dateString || !timeString) {
    return null;
  }

  try {
    const combined = `${dateString}T${timeString}`;
    return dayjs(combined, `${dateFormat}T${timeFormat}`, true);
  } catch {
    return null;
  }
};

export const formatDayjsToString = (dayjsObj: dayjs.Dayjs | null, format: string): string => {
  if (!isValidDayjs(dayjsObj)) {
    return '';
  }

  try {
    return dayjsObj?.format(format) ?? '';
  } catch {
    return '';
  }
};

export const convertUtcToTimezone = (utcDate: string, timezone: string): dayjs.Dayjs | null => {
  if (!utcDate) {
    return null;
  }

  const utcParsed = dayjs.utc(utcDate);
  if (!utcParsed.isValid()) {
    return null;
  }

  const timezoneParsed = utcParsed.tz(timezone);
  return timezoneParsed.isValid() ? timezoneParsed : null;
};

export const convertTimezoneToUtc = (
  dateString: string,
  timeString: string,
  timezone: string
): dayjs.Dayjs | null => {
  if (!dateString || !timeString) {
    return null;
  }

  const combined = `${dateString}T${timeString}`;
  const parsed = dayjs.tz(combined, timezone);
  if (!parsed.isValid()) {
    return null;
  }

  const utcParsed = parsed.utc();
  return utcParsed.isValid() ? utcParsed : null;
};

export const convertBetweenTimezones = (
  dateString: string,
  timeString: string,
  fromTimezone: string,
  toTimezone: string
): dayjs.Dayjs | null => {
  if (!dateString || !timeString) {
    return null;
  }

  const combined = `${dateString}T${timeString}`;
  const fromTzParsed = dayjs.tz(combined, fromTimezone);
  if (!fromTzParsed.isValid()) {
    return null;
  }

  const toTzParsed = fromTzParsed.tz(toTimezone);
  return toTzParsed.isValid() ? toTzParsed : null;
};

export const createUtcDayjs = (date?: string | Date | dayjs.Dayjs) => {
  if (!date) {
    return dayjs.utc();
  }

  return dayjs.utc(date);
};

export const createTimezoneDayjs = (date: string | Date | dayjs.Dayjs, timezone: string) =>
  dayjs.tz(date, timezone);

export const createDayjsInstance = (date?: string | Date | dayjs.Dayjs) => {
  if (!date) {
    return dayjs();
  }

  return dayjs(date);
};
